import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { SearchField } from '../../../shared';
import { SettingsSection } from '../SettingsSection';
import { ChannelPreferences } from './ChannelPreferences';
import { useChannelDisclosure } from './useChannelDisclosure';
import { useChannelNotificationPreferences } from './useChannelNotificationPreferences';

import classes from './ChannelsSection.module.scss';

export const ChannelsSection: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'pushNotifications' });

  const {
    channels,
    isLoadingChannels,
    isChannelEnabled,
    isStoryEnabled,
    setChannelEnabled,
    setStoryEnabled,
  } = useChannelNotificationPreferences();

  const [searchQuery, setSearchQuery] = useState('');

  const isSearchVisible = channels.length > 3;

  const matchedChannels = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return channels.flatMap((channel) => {
      if (!query || channel.name.toLowerCase().includes(query)) {
        return [{ channel, matchedStories: channel.stories }];
      }

      const matchedStories = channel.stories.filter(({ title }) =>
        title.toLowerCase().includes(query)
      );

      if (!matchedStories.length) {
        return [];
      }

      return [{ channel, matchedStories }];
    });
  }, [channels, searchQuery]);

  const { isChannelOpen, toggleChannelOpen } = useChannelDisclosure({
    searchQuery,
    matchedChannelIds: matchedChannels.map(({ channel }) => channel.id),
    ...(!isSearchVisible && { defaultOpenChannelId: channels[0]?.id }),
  });

  const channelPreferences = useMemo(() => {
    if (!matchedChannels.length) {
      return <div className={classes['channels-section__empty']}>{t('common.nothing-found')}</div>;
    }

    return matchedChannels.map(({ channel, matchedStories }) => {
      const { id, name, stories } = channel;

      return (
        <ChannelPreferences
          key={id}
          channelId={id}
          name={name}
          stories={matchedStories}
          enabledStoriesCount={stories.filter(isStoryEnabled).length}
          totalStoriesCount={stories.length}
          isChannelEnabled={isChannelEnabled(channel)}
          isStoryEnabled={isStoryEnabled}
          isOpen={isChannelOpen(id)}
          onOpenToggle={() => toggleChannelOpen(id)}
          onChannelChange={setChannelEnabled}
          onStoryChange={setStoryEnabled}
        />
      );
    });
  }, [
    isChannelEnabled,
    isChannelOpen,
    isStoryEnabled,
    matchedChannels,
    setChannelEnabled,
    setStoryEnabled,
    t,
    toggleChannelOpen,
  ]);

  const search = useMemo(() => {
    if (!isSearchVisible) {
      return null;
    }

    return (
      <SearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t('common.type-to-search')}
        className={classes['channels-section__search']}
      />
    );
  }, [isSearchVisible, searchQuery, t]);

  const body = useMemo(() => {
    if (isLoadingChannels) {
      return (
        <Skeleton
          containerClassName={classes['channels-section__loader']}
          height={'4rem'}
          count={3}
          inline
        />
      );
    }

    return (
      <>
        {search}
        <div className={classes['channels-section__channels']}>{channelPreferences}</div>
      </>
    );
  }, [channelPreferences, isLoadingChannels, search]);

  if (!isLoadingChannels && !channels.length) {
    return null;
  }

  return (
    <SettingsSection title={ctxT('stories-title')} hint={ctxT('stories-hint')}>
      {body}
    </SettingsSection>
  );
};
